// "Kırılıma dönüştür" sihirbazı (spec §6.4, §18 DR6). Durum makinesi:
//   1 kalıp + hedef → İleri → 2 boyut + grup adı + üye adları → İleri → 3 önizleme (girişte dryRun,
//   iskelet "N set taranıyor…") → "Dönüştür" (uygulama, aynı gövde) → done (kalıcı başarı paneli,
//   dokunulmayan formüller listesi; "Tamam" kapatır).
// Hata dalları (adım 3'te bant): 409 `breakdown_convert_category_has_values` → yönlendirme + "Bu
// seçeneğe geç" (hedef = yeni toplam, adım 1); 422 `breakdown_unit_mismatch` → [Birim değişimine
// git]; 403 kategori kapısı → "Yalnız Sistem Yöneticisi"; diğer → 21 kodluk tablo. Sunucu tek tx —
// hata sonrası hiçbir şey yazılmamıştır. Çift gönderim kilidi + istek sürerken kapanış yok
// (BreakdownGroupModal kalıbı). `window.confirm` yok.

import { useEffect, useMemo, useRef, useState } from "react";
import type {
  KpiBreakdownConvertBody,
  KpiBreakdownConvertResult,
  KpiMetric,
  KpiRootCode,
} from "@mahir/shared";
import { rootCodeOf, type CustomDimensionOption } from "../../lib/kpi-breakdown-dimension-ui";
import { useKpiBreakdownDimensionsStore } from "../../stores/kpi-breakdown-dimensions-store";
import { KPI_BREAKDOWN_COPY } from "../../lib/kpi-breakdown-copy";
import {
  buildConvertBody,
  convertErrorOf,
  convertFormGate,
  initialConvertForm,
  markDerivedMembers,
  type ConvertError,
  type ConvertForm,
  type ConvertPending,
  type ConvertStep,
} from "../../lib/kpi-breakdown-convert";
import {
  catalogBreakdownView,
  convertTargetOptions,
  type ConvertPattern,
  type ConvertTargetOption,
} from "../../lib/kpi-catalog-breakdown";
import { createSubmitGuard, guardedClose } from "./BreakdownGroupModal";
import {
  ConvertStepDimension,
  ConvertStepPattern,
  ConvertStepPreview,
  ConvertSuccessPanel,
  convertHelp,
} from "./BreakdownConvertSteps";
import { KpiBreakdownConvertedBand } from "./KpiBreakdownConvertedBand";
import { KpiModal } from "./KpiModal";
import { btnGhost, btnPrimary, btnSmall } from "./kpi-form-styles";

const C = KPI_BREAKDOWN_COPY.convert;

export interface BreakdownConvertDialogViewProps {
  source: KpiMetric;
  form: ConvertForm;
  targets: readonly ConvertTargetOption[];
  step: ConvertStep;
  pending: ConvertPending;
  preview: KpiBreakdownConvertResult | null;
  result: KpiBreakdownConvertResult | null;
  error: ConvertError | null;
  scanSets: number | null;
  /** toplamın kök kategorisi (boyut listesi bölümleri) */
  rootCode?: KpiRootCode | null | undefined;
  customDimensions?: readonly CustomDimensionOption[] | undefined;
  onForm: (form: ConvertForm) => void;
  onNext: () => void;
  onBack: () => void;
  onCommit: () => void;
  onSwitchToNewTotal: () => void;
  onGoToUnit: (metricId: string | null, clientId: string | null) => void;
  onClose: () => void;
}

/** Görünüm (SSR testleri durumu doğrudan verir). */
export function BreakdownConvertDialogView(props: BreakdownConvertDialogViewProps): JSX.Element {
  const { form, step, pending, preview, error } = props;
  const locked = pending !== null || step === 3 || step === "done";
  const gate = step === 2 ? convertFormGate(form) : { ok: true, reason: null };
  const canCommit = step === 3 && pending === null && preview !== null && !preview.noop && !error;
  const stepIndex = step === "done" ? 3 : step;
  const btn = (enabled: boolean) => ({ ...btnPrimary, opacity: enabled ? 1 : 0.5 });

  const footer =
    step === "done" ? (
      <button type="button" style={btnPrimary} onClick={props.onClose}>
        {C.done}
      </button>
    ) : (
      <>
        <span style={{ ...convertHelp, marginRight: "auto" }} aria-live="polite">
          {step === 2 && !gate.ok ? gate.reason : ""}
        </span>
        {step === 1 ? (
          <button
            type="button"
            style={btnGhost}
            disabled={pending !== null}
            onClick={props.onClose}
          >
            {C.cancel}
          </button>
        ) : (
          <button type="button" style={btnGhost} disabled={pending !== null} onClick={props.onBack}>
            {C.back}
          </button>
        )}
        {step === 3 ? (
          <button
            type="button"
            style={btn(canCommit)}
            disabled={!canCommit}
            aria-busy={pending === "commit" || undefined}
            data-testid="kpi-convert-commit"
            onClick={props.onCommit}
          >
            {pending === "commit" ? C.committing : C.commit}
          </button>
        ) : (
          <button
            type="button"
            style={btn(gate.ok && pending === null)}
            disabled={!gate.ok || pending !== null}
            data-testid="kpi-convert-next"
            onClick={props.onNext}
          >
            {C.next}
          </button>
        )}
      </>
    );

  return (
    <KpiModal
      open
      kicker={C.kicker}
      title={C.title(props.source.label)}
      description={C.stepLine(stepIndex, C.steps[stepIndex - 1]!)}
      width={640}
      dirty={step !== 1 && step !== "done" && pending === null}
      onClose={guardedClose(pending, props.onClose)}
      footer={footer}
    >
      <div
        className="grid gap-3"
        style={{ fontSize: "1rem" }}
        data-testid={`kpi-convert-step-${String(step)}`}
      >
        {step === 1 ? (
          <ConvertStepPattern
            source={props.source}
            form={form}
            targets={props.targets}
            locked={locked}
            onForm={props.onForm}
          />
        ) : null}
        {step === 2 ? (
          <ConvertStepDimension
            form={form}
            locked={pending !== null}
            rootCode={props.rootCode}
            custom={props.customDimensions}
            onForm={props.onForm}
          />
        ) : null}
        {step === 3 ? (
          <ConvertStepPreview
            preview={preview}
            scanning={pending === "preview"}
            scanSets={props.scanSets}
          />
        ) : null}
        {step === "done" && props.result ? (
          <ConvertSuccessPanel result={props.result} groupLabel={form.group.label.trim()} />
        ) : null}
        {error && step === 3 ? <ConvertErrorBand {...props} error={error} /> : null}
      </div>
    </KpiModal>
  );
}

function ConvertErrorBand(
  props: { error: ConvertError } & Pick<
    BreakdownConvertDialogViewProps,
    "form" | "onSwitchToNewTotal" | "onGoToUnit"
  >,
): JSX.Element {
  const { error } = props;
  return (
    <div className="grid gap-2" data-testid={`kpi-convert-error-${error.kind}`}>
      <KpiBreakdownConvertedBand kind="failed" detail={error.text} />
      {error.kind === "has_values" && props.form.targetId !== "" ? (
        <button
          type="button"
          style={{ ...btnSmall, minHeight: 44, justifySelf: "start" }}
          onClick={props.onSwitchToNewTotal}
        >
          {C.switchToNewTotal}: {C.newTotal}
        </button>
      ) : null}
      {error.kind === "unit" ? (
        <button
          type="button"
          style={{ ...btnSmall, minHeight: 44, justifySelf: "start" }}
          data-testid="kpi-convert-goto-unit"
          onClick={() => props.onGoToUnit(error.metricId, error.clientId)}
        >
          {C.goToUnit}
        </button>
      ) : null}
    </div>
  );
}

export interface BreakdownConvertDialogProps {
  source: KpiMetric;
  pattern: ConvertPattern;
  catalog: readonly KpiMetric[];
  /** iskelet "N set taranıyor…" — bilinmiyorsa null (genel metin) */
  scanSets?: number | null | undefined;
  /** store `convertCatalogBreakdown` */
  submit: (metricId: string, body: KpiBreakdownConvertBody) => Promise<KpiBreakdownConvertResult>;
  /** uygulama başarılı (katalog yeniden yüklenir; panel açık kalır) */
  onConverted: (result: KpiBreakdownConvertResult) => void;
  /** 422 birim — birim değişimi yüzeyine git (müşteri kaydı ya da katalog göstergesi) */
  onGoToUnit: (metricId: string | null, clientId: string | null) => void;
  onClose: () => void;
}

export function BreakdownConvertDialog(props: BreakdownConvertDialogProps): JSX.Element {
  const { source, catalog } = props;
  const view = useMemo(() => catalogBreakdownView(catalog), [catalog]);
  const targets = useMemo(
    () => (props.pattern === "category" ? convertTargetOptions(source, catalog, view) : []),
    [props.pattern, source, catalog, view],
  );
  const [form, setForm] = useState(() => initialConvertForm(source, props.pattern, catalog, view));
  const [step, setStep] = useState<ConvertStep>(1);
  const [pending, setPending] = useState<ConvertPending>(null);
  const [preview, setPreview] = useState<KpiBreakdownConvertResult | null>(null);
  const [result, setResult] = useState<KpiBreakdownConvertResult | null>(null);
  const [error, setError] = useState<ConvertError | null>(null);
  const guard = useRef(createSubmitGuard());
  const customDimensions = useKpiBreakdownDimensionsStore((s) => s.custom);
  const loadDimensions = useKpiBreakdownDimensionsStore((s) => s.load);
  useEffect(() => {
    void loadDimensions();
  }, [loadDimensions]);
  const rootCode = useMemo(() => rootCodeOf(source.id, catalog), [source.id, catalog]);

  async function run(dryRun: boolean): Promise<void> {
    await guard.current(async () => {
      setPending(dryRun ? "preview" : "commit");
      setError(null);
      try {
        const r = await props.submit(source.id, buildConvertBody(form, dryRun));
        if (dryRun) {
          const next = markDerivedMembers(form, r.derivedMembers ?? []);
          if (next !== form) {
            // müşteri bazlı türetilmiş üye: seçim güncellenir, adım 2'ye dönülür (K-B7)
            setForm(next);
            setPreview(null);
            setStep(2);
          } else setPreview(r);
        } else {
          setResult(r);
          setStep("done");
          props.onConverted(r);
        }
      } catch (err) {
        setError(convertErrorOf(err));
      } finally {
        setPending(null);
      }
    });
  }

  return (
    <BreakdownConvertDialogView
      source={source}
      form={form}
      targets={targets}
      step={step}
      pending={pending}
      preview={preview}
      result={result}
      error={error}
      scanSets={props.scanSets ?? null}
      rootCode={rootCode}
      customDimensions={customDimensions}
      onForm={setForm}
      onNext={() => {
        if (step === 1) setStep(2);
        else if (step === 2) {
          setStep(3);
          setPreview(null);
          void run(true);
        }
      }}
      onBack={() => {
        setError(null);
        setPreview(null);
        setStep(step === 3 ? 2 : 1);
      }}
      onCommit={() => void run(false)}
      onSwitchToNewTotal={() => {
        setForm({ ...form, targetId: "" });
        setError(null);
        setPreview(null);
        setStep(1);
      }}
      onGoToUnit={props.onGoToUnit}
      onClose={props.onClose}
    />
  );
}
